/**
 * ConfirmSheet
 *
 * A confirmation sheet with customizable title, message, and buttons.
 * Returns a Promise that resolves with ExpauseSheetResponse or null.
 *
 * Matches Flutter implementation: lib/presentation/bottom_sheet/confirm_bottom_sheet.dart
 */

import { SheetService } from '../service/SheetService';
import { type ExpauseSheetResponse } from '../service/config/ExpauseSheet.types';
import { ConfirmContent } from './components/ConfirmContent';

/**
 * Configuration for showing a confirm sheet
 */
export interface ConfirmSheetConfig {
  /** Title displayed at the top of the sheet */
  title: string;

  /** Confirmation text/message displayed in the sheet body */
  confirmText: string;

  /** Text for the confirm/ok button (default: 'Ok') */
  okText?: string;

  /** Text for the cancel button (default: 'Cancel') */
  cancelText?: string;

  /** Optional async callback to execute when confirm is clicked */
  onConfirm?: () => Promise<void>;

  /** Where to show the loading indicator: 'button' (inline) or 'overlay' (covers content) */
  loadingStyle?: 'button' | 'overlay';

  /** Optional container element, screen key string, or null */
  container?: HTMLElement | string | null;

  /** Optional screen key of the route opening this sheet */
  screenKey?: string;

  /**
   * Optional stable QA id for THIS confirm sheet's content root. When provided,
   * `ConfirmContent` renders it as a `data-qa-id` on its root element so a
   * UI-test plan can locate this specific confirm sheet (e.g.
   * `data-qa-id="notification-retry-premium-video"`) instead of asserting on its
   * (localized) title text. Test-only; never affects rendering.
   *
   * Distinct from the surface-level `sheet-confirm` wrapper id every confirm
   * sheet shares — that wrapper is not unique per-call, so callers that need to
   * assert on a particular confirm sheet pass their own `qaId` here. Omitting it
   * changes nothing: the wrapper stays `sheet-confirm` and the buttons stay
   * `confirm-sheet-confirm` / `confirm-sheet-cancel`.
   */
  qaId?: string;
}

/**
 * Show a confirmation sheet
 *
 * @param config - Configuration for the confirm sheet
 * @returns Promise that resolves with ExpauseSheetResponse when confirmed, or null when cancelled/dismissed
 *
 * @example
 * // Pattern 1: Caller handles work after sheet closes
 * const response = await ConfirmSheet.show({
 *   title: 'Delete Item',
 *   confirmText: 'Are you sure you want to delete this?',
 * });
 * if (response) {
 *   await deleteItem();
 * }
 *
 * @example
 * // Pattern 2: Sheet handles async work with loading
 * await ConfirmSheet.show({
 *   title: 'Delete Item',
 *   confirmText: 'Are you sure?',
 *   onConfirm: async () => {
 *     await deleteItem();
 *   },
 *   loadingStyle: 'button', // or 'overlay'
 * });
 */
const show = (config: ConfirmSheetConfig): Promise<ExpauseSheetResponse | null> => {
  return new Promise((resolve) => {
    let resolved = false;

    const handleResolve = (response: ExpauseSheetResponse | null) => {
      if (!resolved) {
        resolved = true;
        resolve(response);
      }
    };

    const handleDismiss = () => {
      // Called when sheet is closed via backdrop click, ESC key, etc.
      if (!resolved) {
        resolved = true;
        resolve(null);
      }
    };

    // No title or CTA - everything rendered 
    // inside content so loading overlay covers all
    SheetService.show({
      content: <ConfirmContent config={config} onResolve={handleResolve} />,
      onClose: handleDismiss,
      container: config.container,
      screenKey: config.screenKey,
      closeOnClickOutside: false,
      qaId: 'confirm',
    });
  });
};

export const ConfirmSheet = {
  show,
};
